import React, { useEffect, useState } from 'react';
import { ArrowLeft, BookOpen, Sparkles, Search, Filter, Bookmark } from 'lucide-react';
import { NewsItem, Language } from '../../types';
import { t } from '../../i18n';
import * as footballService from '../../services/footballService';
import SEO from '../Common/SEO';
import { WeeklyMagazineLogo } from '../Common/WeeklyMagazineLogo';

interface MagazineListViewProps {
  onBack: () => void;
  lang: Language;
}

export const MagazineListView: React.FC<MagazineListViewProps> = ({ onBack, lang }) => {
  const [magazines, setMagazines] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const fetchMagazines = async () => {
      try {
        const result = await footballService.fetchSupabaseNewsList(lang, 0, 24, undefined, 'magazine');
        const news = result.news;
        setMagazines(news);
      } catch (error) {
        console.error('Failed to fetch magazines:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchMagazines();
  }, [lang]);

  return (
    <div className="flex flex-col h-full bg-white text-[#2c2c2c]">
      <SEO 
        title={t(lang, 'magazine')} 
        description={t(lang, 'magazineHeroDesc')}
      />
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-100 px-4 py-3 flex items-center gap-2">
        <button 
          onClick={onBack}
          className="p-2 hover:bg-black/5 rounded-full transition-colors"
        >
          <ArrowLeft size={20} />
        </button>
        <div className="flex items-center gap-3">
          <div className="flex flex-col">
            <h1 className="text-xl font-black tracking-tight text-slate-900">{t(lang, 'magazine')} {t(lang, 'magazineCollection')}</h1>
          </div>
        </div>
        <div className="w-10"></div> {/* Spacer for symmetry */}
      </header>

      {/* Hero Section / Visual Entrance */}
      <section className="relative px-6 py-16 text-center overflow-hidden bg-[#121212] text-white">
        {/* Abstract Background Decor */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden opacity-20 pointer-events-none">
          <div className="absolute -top-24 -left-24 w-64 h-64 bg-rose-600 rounded-full blur-[100px]" />
          <div className="absolute top-1/2 -right-32 w-80 h-80 bg-blue-600 rounded-full blur-[120px]" />
        </div>
        
        <div className="relative z-10 flex flex-col items-center">
          <div className="mb-8 transform hover:scale-105 transition-transform duration-500">
            <WeeklyMagazineLogo size={80} className="shadow-[0_20px_50px_rgba(0,0,0,0.5)] border border-white/10" />
          </div>

          <h2 className="text-5xl md:text-7xl font-black tracking-[0.05em] leading-[0.9] uppercase mb-8">
            <span className="block mb-2">{t(lang, 'magazineDiscover')}</span>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-rose-500 via-rose-300 to-rose-600">
              {t(lang, 'magazineDeepStories')}
            </span>
          </h2>
          
          <div className="flex items-center justify-center gap-4 w-full max-sm mx-auto pt-6 border-t border-white/10">
            <p className="text-xs text-white/50 font-medium leading-relaxed uppercase tracking-widest">
              {t(lang, 'magazineHeroDesc')}
            </p>
          </div>
        </div>

        {/* Decorative corner accents */}
        <div className="absolute bottom-4 left-4 flex items-center gap-2 text-[8px] font-black text-white/20 uppercase tracking-[0.3em] vertical-rl rotate-180">
          <Bookmark size={8} /> EST. 2026
        </div>
      </section>

      {/* Magazine Grid - Refined Luxury UI */}
      <main className="flex-1 p-4 md:p-8 pb-40 max-w-5xl mx-auto w-full">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-12">
          {loading ? (
            Array(8).fill(0).map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="aspect-[3/4.2] bg-slate-100 rounded-lg mb-4 shadow-sm"></div>
                <div className="h-3 bg-slate-100 rounded w-3/4 mb-2"></div>
                <div className="h-2 bg-slate-100 rounded w-1/2"></div>
              </div>
            ))
          ) : (
            magazines.map((item, idx) => (
              <div 
                key={item.id}
                onClick={() => window.navigateTo(`/feed/item/${item.id}`)}
                className="group cursor-pointer"
              >
                {/* Book Cover Container */}
                <div className="relative mb-5">
                  <div className="relative aspect-[3/4.2] rounded-sm overflow-hidden shadow-[0_15px_35px_rgba(0,0,0,0.12),0_5px_15px_rgba(0,0,0,0.08)] flex">
                    
                    {/* Spine Detail (More Realistic) */}
                    <div className="absolute inset-y-0 left-0 w-[8px] bg-gradient-to-r from-black/40 via-black/20 to-transparent z-40 pointer-events-none"></div>
                    <div className="absolute inset-y-0 left-[2px] w-[1px] bg-white/10 z-40 pointer-events-none"></div>
                    
                    {/* Image */}
                    <div className="w-full h-full overflow-hidden">
                      <img 
                        src={item.image} 
                        alt="" 
                        className="w-full h-full object-cover object-top" 
                      />
                    </div>
                    
                    {/* Elegant Texture/Lighting Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-tr from-black/40 via-transparent to-white/10 z-20 opacity-60 transition-opacity"></div>
                    <div className="absolute inset-0 shadow-[inset_0_0_80px_rgba(0,0,0,0.15)] z-21"></div>
                    
                    {/* Premium Label - Bookmark Style */}
                    <div className="absolute -top-1 right-1 z-30">
                      <div className="relative">
                        <Bookmark size={30} className="text-rose-600 fill-rose-600 drop-shadow-md" />
                        <div className="absolute inset-0 flex items-center justify-center pb-0.5">
                          <span className="text-[10px] font-black text-white italic">
                            {(magazines.length - idx).toString().padStart(2, '0')}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Content Info Overlay - Minimal & Clean */}
                    <div className="absolute inset-x-0 bottom-0 p-4 z-30 bg-gradient-to-t from-black/95 via-black/40 to-transparent pt-12">
                       <div className="flex items-center gap-2 mb-2 overflow-hidden">
                         <div className="h-[1px] w-4 bg-rose-500"></div>
                         <span className="text-[8px] font-bold text-rose-400 uppercase tracking-[0.2em] whitespace-nowrap">{item.time}</span>
                       </div>
                       <h3 className="text-[12px] font-bold text-white leading-[1.3] line-clamp-2">
                         {item.title}
                       </h3>
                    </div>
                  </div>
                  
                  {/* Realistic Book Pages Side Shadow */}
                  <div className="absolute top-[2%] bottom-[2%] -right-[6px] w-[10px] bg-gradient-to-r from-slate-200 to-slate-50 border-y border-slate-200 rounded-r-[1px] shadow-sm z-10"></div>
                </div>

              </div>
            ))
          )}
        </div>
      </main>
    </div>
  );
};
